/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CalendarEvent } from '../housien-models/CalendarEvent';
import type {
  CalendarEventInput,
  CalendarEventUpdateInput,
} from '../housien-models/CalendarEventInput';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPIHousien } from '../core/OpenAPI';
import { request as __request } from '../core/request';
import type { Paginated } from './CalendarService.types';

/**
 * Recurrence scope values actually accepted by the backend
 * (`_VALID_SCOPES` in `housien-backend-api/housien/services/_calendar_helpers.py`)
 * — distinct from the UI-facing `EventRecurrenceScope` ('this'/'following'/'all'),
 * which callers translate via `toBackendScope` (`useAgendaMutations.ts`) before
 * reaching this HTTP boundary.
 */
export type BackendRecurrenceScope = 'all' | 'this_only' | 'this_and_following';

/**
 * List events, optionally filtered by calendar and/or organizer.
 * @param agencyId The agency id.
 * @param calendarId Optional calendar filter.
 * @param organizerEmail Optional organizer email filter (for member event lazy-loading).
 */
export function listEvents(
  agencyId: string,
  calendarId?: string,
  organizerEmail?: string
): CancelablePromise<Paginated<CalendarEvent>> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/events',
    path: { agencyId },
    query: {
      calendar_id: calendarId,
      organizer_email: organizerEmail,
      _limit: 1000,
    },
  });
}

/**
 * Fetch a single event by id.
 * @param agencyId The agency id.
 * @param eventId The event id.
 */
export function getEvent(
  agencyId: string,
  eventId: string
): CancelablePromise<CalendarEvent> {
  return __request(OpenAPIHousien, {
    method: 'GET',
    url: '/agency/{agencyId}/calendar/events/{eventId}',
    path: { agencyId, eventId },
  });
}

/**
 * Create an event.
 * @param agencyId The agency id.
 * @param requestBody The event payload.
 */
export function createEvent(
  agencyId: string,
  requestBody: CalendarEventInput
): CancelablePromise<{ event_id: string }> {
  return __request(OpenAPIHousien, {
    method: 'POST',
    url: '/agency/{agencyId}/calendar/events',
    path: { agencyId },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Update an event, honoring recurrence scope.
 * @param agencyId The agency id.
 * @param eventId The event id.
 * @param requestBody The fields to update.
 * @param scope The recurrence scope (defaults to all).
 * @param occurrence The occurrence ISO datetime (required when scope != all).
 */
export function updateEvent(
  agencyId: string,
  eventId: string,
  requestBody: CalendarEventUpdateInput,
  scope: BackendRecurrenceScope = 'all',
  occurrence?: string
): CancelablePromise<CalendarEvent> {
  return __request(OpenAPIHousien, {
    method: 'PUT',
    url: '/agency/{agencyId}/calendar/events/{eventId}',
    path: { agencyId, eventId },
    query: { scope, occurrence },
    body: requestBody,
    mediaType: 'application/json',
  });
}

/**
 * Delete an event, honoring recurrence scope.
 * @param agencyId The agency id.
 * @param eventId The event id.
 * @param scope The recurrence scope (defaults to all).
 * @param occurrence The occurrence ISO datetime (required when scope != all).
 */
export function deleteEvent(
  agencyId: string,
  eventId: string,
  scope: BackendRecurrenceScope = 'all',
  occurrence?: string
): CancelablePromise<void> {
  return __request(OpenAPIHousien, {
    method: 'DELETE',
    url: '/agency/{agencyId}/calendar/events/{eventId}',
    path: { agencyId, eventId },
    query: { scope, occurrence },
  });
}
